import { useCallback, useEffect, useRef, useState } from 'react'
import type { SidepanelSectionKey } from '../../sidepanel-section-order'

const LOAD_MORE_INCREMENT = 5

interface ProgressiveSectionLimit {
  visibleCount: number
  nextVisibleCount: number
  showMore: () => void
}

interface SectionExpansionState {
  loadMoreCount: number
  expandedInitialLimit: number | null
}

const RESET_EXPANSION_STATE: SectionExpansionState = { loadMoreCount: 0, expandedInitialLimit: null }
const sectionExpansionStates = new Map<SidepanelSectionKey, SectionExpansionState>()

export function getProgressiveSectionItemLimit(initialLimit: number, loadMoreCount: number): number {
  const addedItems = (LOAD_MORE_INCREMENT * loadMoreCount * (loadMoreCount + 1)) / 2
  return initialLimit + addedItems
}

// A "View more" that reveals a couple of rows costs a click to save almost no
// space, so absorb that short tail into the visible list instead.
const ABSORB_REMAINING_THRESHOLD = 3

export function absorbRemainingItems(visibleCount: number, totalCount: number): number {
  const hiddenCount = totalCount - visibleCount
  return hiddenCount > 0 && hiddenCount <= ABSORB_REMAINING_THRESHOLD ? totalCount : visibleCount
}

export function useProgressiveSectionLimit(
  sectionKey: SidepanelSectionKey,
  initialLimit: number,
  isCollapsed = false,
): ProgressiveSectionLimit {
  const [expansionState, setExpansionState] = useState<SectionExpansionState>(
    () => sectionExpansionStates.get(sectionKey) ?? RESET_EXPANSION_STATE,
  )
  const updateExpansionState = useCallback(
    (update: (current: SectionExpansionState) => SectionExpansionState) => {
      setExpansionState((current) => {
        const next = update(current)
        sectionExpansionStates.set(sectionKey, next)
        return next
      })
    },
    [sectionKey],
  )

  useEffect(() => {
    if (!isCollapsed) {
      return
    }
    updateExpansionState(() => RESET_EXPANSION_STATE)
  }, [isCollapsed, updateExpansionState])

  // Before the user chooses "View more", Home previews follow the live panel
  // layout. The first explicit expansion locks its base so a later resize
  // doesn't unexpectedly remove rows the user asked to reveal.
  const limitBase = expansionState.expandedInitialLimit ?? initialLimit
  const liveCount = getProgressiveSectionItemLimit(limitBase, expansionState.loadMoreCount)
  const nextVisibleCount = getProgressiveSectionItemLimit(limitBase, expansionState.loadMoreCount + 1)
  // The fold is still measuring this list, so keep the last open row count on
  // screen while stored expansion resets for the next open.
  const lastOpenCountRef = useRef(liveCount)
  if (!isCollapsed) {
    lastOpenCountRef.current = liveCount
  }
  const showMore = useCallback(() => {
    updateExpansionState((current) => ({
      expandedInitialLimit: current.expandedInitialLimit ?? initialLimit,
      loadMoreCount: current.loadMoreCount + 1,
    }))
  }, [initialLimit, updateExpansionState])

  return {
    visibleCount: isCollapsed ? lastOpenCountRef.current : liveCount,
    nextVisibleCount,
    showMore,
  }
}
